/* src/pages/admin/AdminDispatcherDetailPage.tsx */
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import DataTable, { TableColumn } from "react-data-table-component";
import AdminDashboardLayout from "../../components/admin/AdminDashboardLayout";
import DashboardCard from "../../components/common/DashboardCard";
import { useAdminAuth } from "../../context/AdminAuthContext";
import { permissionCheckerService } from "../../services/permission.checker.service";
import {
    adminDispatcherService,
    AdminDispatcher
} from "../../services/admin.dispatcher.service";
import {
    adminDispatcherVehicleService,
    DispatcherVehicle,
    GetDispatcherVehiclesParams
} from "../../services/admin.dispatcher.vehicle.service";
import { errorService } from "../../services/error.service";
import Swal from "sweetalert2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faArrowLeft,
    faTruckFast,
    faCircleNotch,
    faTriangleExclamation,
    faUser,
    faAddressCard,
    faGlobe,
    faPlus,
    faEdit,
    faTrash,
    faUserCheck,
    faUserSlash,
    faCar,
    faSearch
} from "@fortawesome/free-solid-svg-icons";
import DispatcherVehicleModal from "../../components/admin/DispatcherVehicleModal";
import styles from "../../styles/pages/AdminDispatcherDetailPage.module.css";
import dispatchersStyles from "../../styles/pages/AdminDispatchersPage.module.css";
import btnStyles from "../../styles/components/Button.module.css";

const AdminDispatcherDetailPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { adminData } = useAdminAuth();

    const [dispatcher, setDispatcher] = useState<AdminDispatcher | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Vehicles states
    const [vehicles, setVehicles] = useState<DispatcherVehicle[]>([]);
    const [vehiclesLoading, setVehiclesLoading] = useState(false);
    const [vehiclesError, setVehiclesError] = useState<string | null>(null);
    const [vehiclesTotalRows, setVehiclesTotalRows] = useState(0);
    const [vehiclesPerPage, setVehiclesPerPage] = useState(10);
    const [vehiclesCurrentPage, setVehiclesCurrentPage] = useState(1);
    const [vehicleSearchText, setVehicleSearchText] = useState("");
    const [vehicleStatusFilter, setVehicleStatusFilter] = useState<string>("");

    // Modal state
    const [showVehicleModal, setShowVehicleModal] = useState(false);
    const [selectedVehicle, setSelectedVehicle] = useState<DispatcherVehicle | null>(null);

    // Permissions check
    const hasVehiclesRead = permissionCheckerService.checkPermission(adminData, "DISPATCHERS", "VEHICLES READ");
    const canCreateVehicle = permissionCheckerService.checkPermission(adminData, "DISPATCHERS", "VEHICLES CREATE");
    const canUpdateVehicle = permissionCheckerService.checkPermission(adminData, "DISPATCHERS", "VEHICLES UPDATE");
    const canDeleteVehicle = permissionCheckerService.checkPermission(adminData, "DISPATCHERS", "VEHICLES DELETE");

    const fetchDetails = async () => {
        if (!id) return;
        try {
            setLoading(true);
            setError(null);
            const response = await adminDispatcherService.getDispatcherById(Number(id));
            if (response.statusCode === 200) {
                setDispatcher(response.data);
            } else {
                setError(response.message || "No se pudo cargar el transportista.");
            }
        } catch (err: any) {
            console.error("Error fetching dispatcher details:", err);
            setError(errorService.getDisplayMessage(err));
        } finally {
            setLoading(false);
        }
    };

    const fetchVehicles = async (page: number, size: number) => {
        if (!id || !hasVehiclesRead) return;
        try {
            setVehiclesLoading(true);
            setVehiclesError(null);

            const params: GetDispatcherVehiclesParams = {
                idDispatcher: Number(id),
                page: page,
                pageSize: size,
                status: vehicleStatusFilter === "" ? undefined : Number(vehicleStatusFilter),
                SearchString: vehicleSearchText || undefined,
            };

            const response = await adminDispatcherVehicleService.getDispatcherVehicles(params);

            if (response.statusCode === 200) {
                setVehicles(response.data);
                setVehiclesTotalRows(response.pagination.totalCount);
            } else {
                setVehiclesError(errorService.getDisplayMessage(response));
            }
        } catch (err: any) {
            console.error("Error fetching vehicles:", err);
            setVehiclesError(errorService.getDisplayMessage(err));
        } finally {
            setVehiclesLoading(false);
        }
    };

    useEffect(() => {
        fetchDetails();
    }, [id]);

    useEffect(() => {
        if (id && hasVehiclesRead) {
            fetchVehicles(1, vehiclesPerPage);
        }
    }, [id, hasVehiclesRead, vehicleStatusFilter, vehiclesPerPage]);

    const handleVehiclePageChange = (page: number) => {
        setVehiclesCurrentPage(page);
        fetchVehicles(page, vehiclesPerPage);
    };

    const handleVehiclePerRowsChange = async (newPerPage: number, page: number) => {
        setVehiclesPerPage(newPerPage);
        fetchVehicles(page, newPerPage);
    };

    const handleVehicleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        setVehiclesCurrentPage(1);
        fetchVehicles(1, vehiclesPerPage);
    };

    const handleVehicleToggleStatus = async (vehicle: DispatcherVehicle) => {
        const newStatus = vehicle.status === 1 ? 0 : 1;
        const actionText = newStatus === 1 ? "activar" : "desactivar";

        const result = await Swal.fire({
            title: "¿Estás seguro?",
            text: `Esta acción procederá a ${actionText} el vehículo "${vehicle.name}".`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: newStatus === 1 ? "#10b981" : "#f59e0b",
            cancelButtonColor: "#94a3b8",
            confirmButtonText: `Sí, ${actionText}`,
            cancelButtonText: "Cancelar",
        });

        if (result.isConfirmed) {
            try {
                setVehiclesLoading(true);
                const response = await adminDispatcherVehicleService.changeDispatcherVehicleStatus({
                    id: vehicle.id,
                    status: newStatus,
                });

                if (response.statusCode === 200) {
                    await Swal.fire({
                        icon: "success",
                        title: newStatus === 1 ? "Activado" : "Desactivado",
                        text: `El vehículo ha sido ${newStatus === 1 ? "activado" : "desactivado"} con éxito.`,
                    });
                    fetchVehicles(vehiclesCurrentPage, vehiclesPerPage);
                } else {
                    errorService.showErrorAlert(response, "Error");
                }
            } catch (err) {
                console.error("Error updating vehicle status:", err);
                errorService.showErrorAlert(err, "Error");
            } finally {
                setVehiclesLoading(false);
            }
        }
    };

    const handleVehicleDelete = async (vehicle: DispatcherVehicle) => {
        const result = await Swal.fire({
            title: "¿Eliminar vehículo?",
            text: `Esta acción eliminará de forma lógica el vehículo "${vehicle.name}".`,
            icon: "error",
            showCancelButton: true,
            confirmButtonColor: "#ef4444",
            cancelButtonColor: "#94a3b8",
            confirmButtonText: "Sí, eliminar",
            cancelButtonText: "Cancelar",
        });

        if (result.isConfirmed) {
            try {
                setVehiclesLoading(true);
                const response = await adminDispatcherVehicleService.deleteDispatcherVehicle({
                    id: vehicle.id,
                });

                if (response.statusCode === 200) {
                    await Swal.fire({
                        icon: "success",
                        title: "Eliminado",
                        text: "El vehículo ha sido eliminado con éxito.",
                    });
                    fetchVehicles(vehiclesCurrentPage, vehiclesPerPage);
                } else {
                    errorService.showErrorAlert(response, "Error");
                }
            } catch (err) {
                console.error("Error deleting vehicle:", err);
                errorService.showErrorAlert(err, "Error");
            } finally {
                setVehiclesLoading(false);
            }
        }
    };

    const handleBack = () => {
        navigate("/admin/dispatchers");
    };

    const columns: TableColumn<DispatcherVehicle>[] = [
        {
            name: "Vehículo",
            selector: (row) => row.name,
            sortable: true,
            cell: (row) => (
                <div className="d-flex flex-column py-2">
                    <span className={dispatchersStyles.dispatcherName}>{row.name}</span>
                    {row.description && <span className={dispatchersStyles.dispatcherDni}>{row.description}</span>}
                </div>
            ),
            minWidth: "250px",
        },
        {
            name: "Estado",
            selector: (row) => row.status,
            sortable: true,
            cell: (row) => (
                <span className={`${dispatchersStyles.badge} ${row.status === 1 ? dispatchersStyles.statusActive : dispatchersStyles.statusInactive}`}>
                    {row.status === 1 ? "Activo" : "Inactivo"}
                </span>
            ),
            width: "120px",
        },
        {
            name: "Acciones",
            width: "180px",
            cell: (row) => (
                <div className="d-flex gap-2">
                    {canUpdateVehicle && (
                        <button
                            className={dispatchersStyles.detailButton}
                            onClick={() => {
                                setSelectedVehicle(row);
                                setShowVehicleModal(true);
                            }}
                            title="Editar vehículo"
                        >
                            <FontAwesomeIcon icon={faEdit} />
                        </button>
                    )}
                    {canUpdateVehicle && (
                        <button
                            className={row.status === 1 ? dispatchersStyles.deactivateButton : dispatchersStyles.activateButton}
                            onClick={() => handleVehicleToggleStatus(row)}
                            title={row.status === 1 ? "Desactivar" : "Activar"}
                        >
                            <FontAwesomeIcon icon={row.status === 1 ? faUserSlash : faUserCheck} />
                        </button>
                    )}
                    {canDeleteVehicle && (
                        <button
                            className={dispatchersStyles.deleteButton}
                            onClick={() => handleVehicleDelete(row)}
                            title="Eliminar"
                        >
                            <FontAwesomeIcon icon={faTrash} />
                        </button>
                    )}
                </div>
            ),
            right: true,
        }
    ];

    const customStyles = {
        headRow: {
            style: {
                backgroundColor: "var(--elPaket-primary-color)",
                color: "white",
                minHeight: "48px",
            },
        },
        headCells: {
            style: {
                color: "var(--brand-black)",
                fontWeight: "700",
                fontSize: "12px",
                textTransform: "uppercase" as any,
                letterSpacing: "0.5px",
            },
        },
        rows: {
            style: {
                minHeight: "60px",
                "&:hover": {
                    backgroundColor: "#f8fafc !important",
                },
            },
        },
    };

    return (
        <AdminDashboardLayout>
            <div className={styles.detailContainer}>
                <DashboardCard
                    icon={faTruckFast}
                    title={dispatcher ? `Detalles: ${dispatcher.firstName} ${dispatcher.lastName}` : "Detalles del Transportista"}
                    description={dispatcher ? `ID de Transportista: #${dispatcher.id}` : "Información del transportista afiliado."}
                />

                {loading ? (
                    <div className={styles.loadingContainer}>
                        <FontAwesomeIcon icon={faCircleNotch} spin size="3x" className={styles.loadingIcon} />
                        <p>Cargando detalles del transportista...</p>
                    </div>
                ) : error ? (
                    <div className={styles.errorContainer}>
                        <FontAwesomeIcon icon={faTriangleExclamation} size="3x" />
                        <h3>Error</h3>
                        <p>{error}</p>
                        <button className={btnStyles.primary} onClick={handleBack}>
                            Regresar
                        </button>
                    </div>
                ) : dispatcher ? (
                    <>
                        <div className={styles.detailCard}>
                             <div className={styles.cardBody}>
                                 <h3 className={styles.sectionTitle}>Información del Transportista</h3>
                                 <div className={styles.infoGrid}>
                                     <div className={styles.infoItem}>
                                         <span className={styles.infoLabel}>Código Transportista</span>
                                         <span className={styles.infoValue}>
                                             <FontAwesomeIcon icon={faAddressCard} className="me-2 text-muted" />
                                             {dispatcher.code || "-"}
                                         </span>
                                     </div>

                                     <div className={styles.infoItem}>
                                         <span className={styles.infoLabel}>Nombre Completo</span>
                                         <span className={styles.infoValue}>
                                             <FontAwesomeIcon icon={faUser} className="me-2 text-muted" />
                                             {dispatcher.firstName} {dispatcher.lastName}
                                         </span>
                                     </div>

                                     <div className={styles.infoItem}>
                                         <span className={styles.infoLabel}>Número de Licencia</span>
                                         <span className={styles.infoValue}>
                                             <FontAwesomeIcon icon={faAddressCard} className="me-2 text-muted" />
                                             {dispatcher.dni}
                                         </span>
                                     </div>

                                     <div className={styles.infoItem}>
                                         <span className={styles.infoLabel}>Teléfono</span>
                                         <span className={styles.infoValue}>
                                             <FontAwesomeIcon icon={faGlobe} className="me-2 text-muted" />
                                             {dispatcher.phoneNumber || "-"}
                                         </span>
                                     </div>

                                     <div className={styles.infoItem}>
                                         <span className={styles.infoLabel}>Correo Electrónico</span>
                                         <span className={styles.infoValue}>
                                             <FontAwesomeIcon icon={faGlobe} className="me-2 text-muted" />
                                             {dispatcher.email || "-"}
                                         </span>
                                     </div>

                                     <div className={styles.infoItem}>
                                         <span className={styles.infoLabel}>Fecha Afiliación</span>
                                         <span className={styles.infoValue}>
                                             <FontAwesomeIcon icon={faAddressCard} className="me-2 text-muted" />
                                             {dispatcher.affiliationDate
                                                 ? new Date(dispatcher.affiliationDate).toLocaleDateString("es-ES", {
                                                       year: "numeric",
                                                       month: "2-digit",
                                                       day: "2-digit",
                                                   })
                                                 : "-"}
                                         </span>
                                     </div>

                                     <div className={styles.infoItem}>
                                         <span className={styles.infoLabel}>País de Operación</span>
                                         <span className={styles.infoValue}>
                                             <FontAwesomeIcon icon={faGlobe} className="me-2 text-muted" />
                                             {dispatcher.iso2Country || dispatcher.isO2Country}
                                         </span>
                                     </div>

                                     <div className={styles.infoItem}>
                                         <span className={styles.infoLabel}>Estado / Provincia</span>
                                         <span className={styles.infoValue}>
                                             <FontAwesomeIcon icon={faGlobe} className="me-2 text-muted" />
                                             {dispatcher.state || "-"}
                                         </span>
                                     </div>

                                     <div className={styles.infoItem}>
                                         <span className={styles.infoLabel}>Código Postal</span>
                                         <span className={styles.infoValue}>
                                             <FontAwesomeIcon icon={faGlobe} className="me-2 text-muted" />
                                             {dispatcher.zipCode || "-"}
                                         </span>
                                     </div>

                                     <div className={styles.infoItem}>
                                         <span className={styles.infoLabel}>Estatus</span>
                                         <div className="mt-1">
                                             <span className={`${styles.badge} ${dispatcher.status === 1 ? styles.statusActive : styles.statusInactive}`}>
                                                 {dispatcher.status === 1 ? "Activo" : "Inactivo"}
                                             </span>
                                         </div>
                                     </div>
                                 </div>
                             </div>
                         </div>

                        {hasVehiclesRead && (
                            <div className={dispatchersStyles.tableWrapper} style={{ marginTop: "24px" }}>
                                <div className="d-flex justify-content-between align-items-center mb-4">
                                    <h3 className={styles.sectionTitle} style={{ borderBottom: "none", marginBottom: 0, paddingBottom: 0 }}>
                                        <FontAwesomeIcon icon={faCar} className="me-2" style={{ color: "var(--brand-black)" }} />
                                        Vehículos del Transportista
                                    </h3>
                                    {canCreateVehicle && (
                                        <button
                                            className={btnStyles.primary}
                                            onClick={() => {
                                                setSelectedVehicle(null);
                                                setShowVehicleModal(true);
                                            }}
                                        >
                                            <FontAwesomeIcon icon={faPlus} className="me-2" /> Registrar Vehículo
                                        </button>
                                    )}
                                </div>

                                <form className={dispatchersStyles.tableActions} onSubmit={handleVehicleSearch}>
                                    <div className={dispatchersStyles.filterSection}>
                                        <div className={dispatchersStyles.filterGroup}>
                                            <label>Estado del Vehículo</label>
                                            <select
                                                className={dispatchersStyles.filterSelect}
                                                value={vehicleStatusFilter}
                                                onChange={(e) => setVehicleStatusFilter(e.target.value)}
                                            >
                                                <option value="">Todos</option>
                                                <option value="1">Activos</option>
                                                <option value="0">Inactivos</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div className={dispatchersStyles.searchRow}>
                                        <div className={dispatchersStyles.searchWrapper}>
                                            <FontAwesomeIcon icon={faSearch} className={dispatchersStyles.searchIcon} />
                                            <input
                                                type="text"
                                                placeholder="Buscar por nombre..."
                                                className={dispatchersStyles.searchInput}
                                                value={vehicleSearchText}
                                                onChange={(e) => setVehicleSearchText(e.target.value)}
                                            />
                                        </div>
                                        <button type="submit" className={btnStyles.primary}>
                                            <FontAwesomeIcon icon={faSearch} /> Buscar
                                        </button>
                                    </div>
                                </form>

                                {vehiclesError ? (
                                    <div className={dispatchersStyles.emptyContainer}>
                                        <FontAwesomeIcon icon={faTriangleExclamation} size="3x" style={{ color: "#ef4444", marginBottom: "16px" }} />
                                        <h3>Error de Carga</h3>
                                        <p>{vehiclesError}</p>
                                        <button className={btnStyles.primary} style={{ margin: "20px auto" }} onClick={() => fetchVehicles(vehiclesCurrentPage, vehiclesPerPage)}>
                                            Reintentar
                                        </button>
                                    </div>
                                ) : vehicles.length === 0 && !vehiclesLoading ? (
                                    <div className={dispatchersStyles.emptyContainer}>
                                        <FontAwesomeIcon icon={faCar} size="3x" style={{ marginBottom: "16px", opacity: 0.5 }} />
                                        <p>No se encontraron vehículos registrados para este transportista.</p>
                                    </div>
                                ) : (
                                    <DataTable
                                        columns={columns}
                                        data={vehicles}
                                        pagination
                                        paginationServer
                                        paginationTotalRows={vehiclesTotalRows}
                                        paginationDefaultPage={vehiclesCurrentPage}
                                        onChangeRowsPerPage={handleVehiclePerRowsChange}
                                        onChangePage={handleVehiclePageChange}
                                        progressPending={vehiclesLoading}
                                        progressComponent={
                                            <div className={dispatchersStyles.loadingContainer}>
                                                <FontAwesomeIcon icon={faCircleNotch} spin size="3x" className={dispatchersStyles.loadingIcon} />
                                                <p>Cargando vehículos...</p>
                                            </div>
                                        }
                                        customStyles={customStyles}
                                        responsive
                                        highlightOnHover
                                        noDataComponent={null}
                                    />
                                )}
                            </div>
                        )}
                    </>
                ) : null}
            </div>

            {dispatcher && (
                <DispatcherVehicleModal
                    show={showVehicleModal}
                    onHide={() => {
                        setShowVehicleModal(false);
                        setSelectedVehicle(null);
                    }}
                    onSuccess={() => fetchVehicles(vehiclesCurrentPage, vehiclesPerPage)}
                    idDispatcher={dispatcher.id}
                    vehicle={selectedVehicle}
                />
            )}
        </AdminDashboardLayout>
    );
};

export default AdminDispatcherDetailPage;
